<div class="bff-test-container">
  <mat-card>
    <mat-card-header>
      <mat-card-title>BFF Integration Test</mat-card-title>
      <mat-card-subtitle>Test StudiesApiService integration with BFF endpoints</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      <p class="info-text">
        This component tests the Angular StudiesApiService integration with the BFF (Backend for Frontend) server.
        Ensure the BFF server is running at <strong>http://localhost:3000</strong> before testing.
      </p>

      <!-- Search Test Section -->
      <mat-expansion-panel [expanded]="true">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>search</mat-icon>
            Test 1: Search Studies (searchStudies)
          </mat-panel-title>
          <mat-panel-description>
            Test the /api/v1/studies endpoint
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="test-section">
          <div class="form-group">
            <mat-form-field appearance="outline">
              <mat-label>Patient Name</mat-label>
              <input matInput [(ngModel)]="searchFilters.patientName">
              <mat-hint>Use wildcards: *Doe*, *John*, etc.</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Patient ID</mat-label>
              <input matInput [(ngModel)]="searchFilters.patientId">
              <mat-hint>e.g. *123*</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Accession Number</mat-label>
              <input matInput [(ngModel)]="searchFilters.accessionNumber">
              <mat-hint>e.g. *ACC*</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Modality</mat-label>
              <input matInput [(ngModel)]="searchFilters.modality">
              <mat-hint>e.g. CT, MR, DX</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Page Size</mat-label>
              <input matInput type="number" [(ngModel)]="searchFilters.pageSize">
              <mat-hint>Default: 10</mat-hint>
            </mat-form-field>
          </div>

          <div class="button-group">
            <button mat-raised-button color="primary" (click)="testSearch()" [disabled]="isSearching">
              <mat-icon>play_arrow</mat-icon>
              {{ isSearching ? 'Searching...' : 'Test Search' }}
            </button>
            <button mat-button (click)="useSearchResultForDownload()" [disabled]="!searchResults?.items?.length">
              <mat-icon>download</mat-icon>
              Use Results for Download Test
            </button>
          </div>

          <div *ngIf="searchError" class="error-message">
            <mat-icon>error</mat-icon>
            <strong>Error:</strong> {{ searchError }}
          </div>

          <div *ngIf="searchResults" class="results-section">
            <h4>Search Results:</h4>
            <div class="result-summary">
              <p><strong>Total Items:</strong> {{ searchResults.items?.length || 0 }}</p>
              <p><strong>Page Size:</strong> {{ searchResults.pageSize }}</p>
              <p *ngIf="searchResults.nextPageToken"><strong>Has Next Page:</strong> Yes</p>
            </div>
            <pre class="json-output">{{ searchResults | json }}</pre>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Study Details Test Section -->
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>info</mat-icon>
            Test 2: Get Study Details (getStudy)
          </mat-panel-title>
          <mat-panel-description>
            Test the /api/v1/studies/:studyInstanceUid endpoint
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="test-section">
          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Study Instance UID</mat-label>
              <input matInput [(ngModel)]="studyInstanceUid">
              <mat-hint>Enter a valid StudyInstanceUID from search results or Orthanc</mat-hint>
            </mat-form-field>
          </div>

          <div class="button-group">
            <button mat-raised-button color="primary" (click)="testGetStudy()" [disabled]="isLoadingStudy">
              <mat-icon>play_arrow</mat-icon>
              {{ isLoadingStudy ? 'Loading...' : 'Test Get Study' }}
            </button>
            <button mat-button (click)="useStudyDetailsForDownload()" [disabled]="!studyDetails">
              <mat-icon>download</mat-icon>
              Use for Download Test
            </button>
          </div>

          <div *ngIf="studyError" class="error-message">
            <mat-icon>error</mat-icon>
            <strong>Error:</strong> {{ studyError }}
          </div>

          <div *ngIf="studyDetails" class="results-section">
            <h4>Study Details:</h4>
            <div class="result-summary">
              <p><strong>Study Instance UID:</strong> {{ studyDetails.studyInstanceUid }}</p>
              <p><strong>Patient Name:</strong> {{ studyDetails.patientName }}</p>
              <p><strong>Patient ID:</strong> {{ studyDetails.patientId }}</p>
              <p><strong>Study Date:</strong> {{ studyDetails.studyDate }}</p>
              <p><strong>Series Count:</strong> {{ studyDetails.series?.length || 0 }}</p>
              <p><strong>Orthanc ID:</strong> {{ studyDetails.orthancId }}</p>
            </div>
            <pre class="json-output">{{ studyDetails | json }}</pre>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Download Test Section -->
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>file_download</mat-icon>
            Test 3: Download Study (downloadStudy)
          </mat-panel-title>
          <mat-panel-description>
            Test the /api/v1/exports/studies endpoint
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="test-section">
          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Study Instance UIDs (one per line)</mat-label>
              <textarea
                matInput
                [(ngModel)]="downloadStudyUidsInput"
                rows="3">
              </textarea>
              <mat-hint>Enter StudyInstanceUIDs to download (one per line, max 25)</mat-hint>
            </mat-form-field>
          </div>

          <div class="button-group">
            <button mat-raised-button color="primary" (click)="testDownload()" [disabled]="isDownloading">
              <mat-icon>play_arrow</mat-icon>
              {{ isDownloading ? 'Downloading...' : 'Test Download' }}
            </button>
          </div>

          <div *ngIf="downloadError" class="error-message">
            <mat-icon>error</mat-icon>
            <strong>Error:</strong> {{ downloadError }}
          </div>

          <div *ngIf="!downloadError && !isDownloading" class="info-message">
            <mat-icon>info</mat-icon>
            <span>Click "Test Download" to download the study(ies) as ZIP file(s). The download will start automatically.</span>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Actions -->
      <div class="actions-section">
        <button mat-button color="warn" (click)="clearResults()">
          <mat-icon>clear</mat-icon>
          Clear All Results
        </button>
      </div>
    </mat-card-content>
  </mat-card>
</div>
